// packages import
import { Box, Button, CircularProgress, DialogActions, Grid, Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'

// component import
import { Alert } from '../../common/Alert'
import { CustomModal } from '../../common/CustomModal'
import { CaseDetailRow } from '../../common/CaseDetailRow'

// others
import {
  useFetchPaymentByIdLazyQuery
} from '../../../../generated'
import { CANCEL, GRAPHQL_QUERY_POLICY, NOT_APPLICABLE } from '../../../constants'
import { formatCurrency, enumToLabel } from '../../../utils'
import dayjs from 'dayjs'
import { CardBox } from '../../../theme/styleComponents'

export interface ViewPaymentModalProps {
  paymentId: string
  isOpen: boolean
  onClose: () => void
}

export const ViewPaymentModal: FC<ViewPaymentModalProps> = ({
  paymentId,
  isOpen,
  onClose
}): JSX.Element => {
  const [paymentData, setPaymentData] = useState<any>(null)

  const [fetchPaymentById, { loading }] = useFetchPaymentByIdLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { fetchPaymentById } = data || {}
      const { payment } = fetchPaymentById || {}
      if (payment) {
        setPaymentData(payment)
      }
    }
  })

  useEffect(() => {
    if (isOpen && paymentId) {
      fetchPaymentById({
        variables: {
          paymentId
        }
      })
    }
  }, [isOpen, paymentId, fetchPaymentById])

  const handleClose = () => {
    setPaymentData(null)
    onClose()
  }

  if (!paymentData && !loading) {
    return (
      <CustomModal title="View Payment" isOpen={isOpen} handleClose={handleClose} maxWidth="md">
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No payment data available</Typography>
        </Box>
        <DialogActions sx={{ padding: '30px 0 0' }}>
          <Button onClick={handleClose}>{CANCEL}</Button>
        </DialogActions>
      </CustomModal>
    )
  }

  const {
    amount,
    returnAmount,
    returnNotes,
    returnedAt,
    date,
    paymentMethod,
    paymentType,
    referenceNumber,
    slipNumber,
    notes,
    reconciledAt,
    createdAt,
    updatedAt,
    account,
    office,
    bill,
    case: matter,
    client,
    vendor,
    creator,
    reconciledBy,
    linkedPayment,
    transaction
  } = paymentData || {}

  const getClientName = () => {
    if (!client) return NOT_APPLICABLE
    return client.displayName || NOT_APPLICABLE
  }

  const getCreatorName = () => {
    if (!creator) return 'System'
    return `${creator.firstName || ''} ${creator.lastName || ''}`.trim() || 'System'
  }

  const getReconciledByName = () => {
    if (!reconciledBy) return NOT_APPLICABLE
    return `${reconciledBy.firstName || ''} ${reconciledBy.lastName || ''}`.trim() || NOT_APPLICABLE
  }

  const getMatterDisplay = () => {
    if (!matter) return NOT_APPLICABLE
    const caseNumber = matter.caseNumber || matter.consultMatterNumber
    return caseNumber ? `#${caseNumber} - ${matter.title || ''}` : matter.title || NOT_APPLICABLE
  }

  const getBillDisplay = () => {
    if (!bill) return NOT_APPLICABLE
    return `#${bill.id} - ${enumToLabel(bill.status || '')} (${formatCurrency(bill.amount || 0)})`
  }

  const getAccountDisplay = () => {
    if (!account) return NOT_APPLICABLE
    return account.sortCode
      ? `(${account.sortCode}) ${account.name || ''}`
      : account.name || NOT_APPLICABLE
  }

  return (
    <CustomModal title="View Payment" isOpen={isOpen} handleClose={handleClose} maxWidth="md">
      {loading ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          <CardBox>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CaseDetailRow
                  title="Amount"
                  content={
                    returnedAt
                      ? `${formatCurrency(returnAmount || 0)} (Returned Check)`
                      : formatCurrency(amount || 0)
                  }
                />
              </Grid>

              {returnedAt && (
                <Grid item xs={12}>
                  <CaseDetailRow title="Return Reason" content={returnNotes || NOT_APPLICABLE} />
                </Grid>
              )}

              <Grid item xs={12}>
                <CaseDetailRow
                  title="Date"
                  content={date ? dayjs(date).format('MM/DD/YYYY') : NOT_APPLICABLE}
                />
              </Grid>

              <Grid item xs={12}>
                <CaseDetailRow title="Payment Method" content={enumToLabel(paymentMethod || '')} />
              </Grid>

              <Grid item xs={12}>
                <CaseDetailRow title="Payment Type" content={enumToLabel(paymentType || '')} />
              </Grid>

              <Grid item xs={12}>
                <CaseDetailRow title="Reference Number" content={referenceNumber || NOT_APPLICABLE} />
              </Grid>

              <Grid item xs={12}>
                <CaseDetailRow title="Slip Number" content={slipNumber || NOT_APPLICABLE} />
              </Grid>

              <Grid item xs={12}>
                <CaseDetailRow
                  title="Destination Account"
                  content={getAccountDisplay()}
                  to={account?.id ? `/accounts/chartered/${account.id}` : undefined}
                />
              </Grid>

              <Grid item xs={12}>
                <CaseDetailRow title="Location" content={office?.name || NOT_APPLICABLE} />
              </Grid>

              {client && (
                <Grid item xs={12}>
                  <CaseDetailRow
                    title="Client"
                    content={getClientName()}
                    to={client.id ? `/clients/client-detail/${client.id}` : undefined}
                  />
                </Grid>
              )}

              {vendor && (
                <Grid item xs={12}>
                  <CaseDetailRow
                    title="Vendor"
                    content={`#${vendor.vendorId} - ${vendor.name || ''}`}
                  />
                </Grid>
              )}

              {matter && (
                <Grid item xs={12}>
                  <CaseDetailRow
                    title="Matter"
                    content={getMatterDisplay()}
                    to={matter.id ? `/matters/matter-detail/${matter.id}` : undefined}
                  />
                </Grid>
              )}

              {bill && (
                <Grid item xs={12}>
                  <CaseDetailRow
                    title="Bill"
                    content={getBillDisplay()}
                    to={bill.id ? `/billing/bill-detail/${bill.id}` : undefined}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <CaseDetailRow title="Created By" content={getCreatorName()} />
              </Grid>

              <Grid item xs={12}>
                <CaseDetailRow
                  title="Created At"
                  content={createdAt ? dayjs(createdAt).format('MM/DD/YYYY hh:mm A') : NOT_APPLICABLE}
                />
              </Grid>

              <Grid item xs={12}>
                <CaseDetailRow
                  title="Updated At"
                  content={updatedAt ? dayjs(updatedAt).format('MM/DD/YYYY hh:mm A') : NOT_APPLICABLE}
                />
              </Grid>

              {reconciledAt && (
                <>
                  <Grid item xs={12}>
                    <CaseDetailRow
                      title="Reconciled At"
                      content={dayjs(reconciledAt).format('MM/DD/YYYY hh:mm A')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CaseDetailRow title="Reconciled By" content={getReconciledByName()} />
                  </Grid>
                </>
              )}


              {linkedPayment && (
                <Grid item xs={12}>
                  <CaseDetailRow
                    title="Linked Payment"
                    content={linkedPayment.account?.name || NOT_APPLICABLE}
                  />
                </Grid>
              )}

              {transaction && (
                <>
                  <Grid item xs={12}>
                    <CaseDetailRow
                      title="Transaction ID"
                      content={transaction.transactionId || NOT_APPLICABLE}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CaseDetailRow
                      title="Transaction Status"
                      content={enumToLabel(transaction.status || '')}
                    />
                  </Grid>
                  {transaction.settledAt && (
                    <Grid item xs={12}>
                      <CaseDetailRow
                        title="Settled At"
                        content={dayjs(transaction.settledAt).format('MM/DD/YYYY hh:mm A')}
                      />
                    </Grid>
                  )}
                </>
              )}

              {notes && (
                <Grid item xs={12}>
                  <CaseDetailRow title="Notes" content={notes} />
                </Grid>
              )}
            </Grid>
          </CardBox>
        </Box>
      )}

      <DialogActions sx={{ padding: '30px 0 0' }}>
        <Button onClick={handleClose}>{CANCEL}</Button>
      </DialogActions>
    </CustomModal>
  )
}

export default ViewPaymentModal
